import { keyframes } from '@emotion/react'
import { SxProps } from '@mui/material'
import { FONT_WEIGHTS } from '@/styles'
import { headerDimensions } from '../Header'
import { BANNER_HEIGHT, bannerClasses } from './Banner.data'

interface BannerStyles {
  banner: SxProps
  bannerFanatics: SxProps
  transparentHeaderSpacer: SxProps
  container: SxProps
  contentWrapper: SxProps
  contentWrapperFanatics: SxProps
  subContentWrapper: SxProps

  subHeading: SxProps
  subHeadingFanatics: SxProps
  animateHeading: SxProps
  animateSubHeading: SxProps
  animateSearch: SxProps
  animateChips: SxProps
  fanaticsIcon: SxProps
}

export const WHITE_BAR_HEIGHT = {
  DESKTOP: 40,
  MOBILE: 20,
}

const CONTENT_WRAPPER_WIDTH = 552

const slideUpFadeIn = keyframes({
  from: { opacity: 0, transform: 'translateY(48px)' },
  to: { opacity: 1, transform: 'translateY(0)' },
})

export const bannerStyles: BannerStyles = {
  transparentHeaderSpacer: {
    height: headerDimensions.height,
  },
  banner: {
    minHeight: {
      xs: BANNER_HEIGHT.MOBILE + WHITE_BAR_HEIGHT.MOBILE,
      lg: BANNER_HEIGHT.DESKTOP + WHITE_BAR_HEIGHT.DESKTOP,
    },
    position: 'relative',
    marginBottom: {
      xs: `-${WHITE_BAR_HEIGHT.MOBILE}px`,
      lg: `-${WHITE_BAR_HEIGHT.DESKTOP}px`,
    },
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',

    [`&.${bannerClasses.mainHeaderTransparent}`]: {
      '@media (orientation: landscape) and (max-height: 600px)': {
        pt: `calc(${headerDimensions.height}px + 30px)`, // header height + some extra spacing
      },
    },

    '.background-image': {
      zIndex: -1,
    },
    '+ *': {
      borderTopLeftRadius: {
        xs: WHITE_BAR_HEIGHT.MOBILE,
        lg: WHITE_BAR_HEIGHT.DESKTOP,
      },
      borderTopRightRadius: {
        xs: WHITE_BAR_HEIGHT.MOBILE,
        lg: WHITE_BAR_HEIGHT.DESKTOP,
      },
    },

    [`&.${bannerClasses.compact}`]: {
      minHeight: {
        xs: BANNER_HEIGHT.COMPACT.MOBILE + WHITE_BAR_HEIGHT.MOBILE,
        lg: BANNER_HEIGHT.COMPACT.DESKTOP + WHITE_BAR_HEIGHT.DESKTOP,
      },
    },
    [`&.${bannerClasses.partners}`]: {
      minHeight: {
        xs: BANNER_HEIGHT.PARTNER.MOBILE + WHITE_BAR_HEIGHT.MOBILE,
        lg: BANNER_HEIGHT.PARTNER.DESKTOP + WHITE_BAR_HEIGHT.DESKTOP,
      },
    },
    [`&.${bannerClasses.fullSize}`]: {
      minHeight: {
        xs: `calc(100vh)`,
        lg: `calc(100vh  + ${WHITE_BAR_HEIGHT.DESKTOP}px)`,
      },
    },
    [`&.${bannerClasses.sticky}`]: {
      position: { xs: 'sticky', md: 'relative' },
      top: { xs: headerDimensions.height, md: 'initial' },
      zIndex: { xs: -1, md: 'initial' },

      [`&.${bannerClasses.mainHeaderTransparent}`]: {
        top: 0,
      },
    },
  },
  bannerFanatics: {
    [`&.${bannerClasses.fullSize}`]: {
      minHeight: {
        lg: `calc(100vh)`,
      },
    },
  },
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    px: { xs: 0 },
  },
  contentWrapper: {
    maxWidth: 'calc(100vw - 40px)',
    transform: {
      xs: `translateY(-${WHITE_BAR_HEIGHT.MOBILE / 2}px)`,
      lg: `translateY(-${WHITE_BAR_HEIGHT.DESKTOP / 2}px)`,
    },
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: {
      xs: 2,
      md: 3,
    },
    px: {
      sm: 2.5,
      xs: 0,
    },
    marginBottom: 2.5,

    [`.${bannerClasses.compact} &`]: {
      gap: {
        xs: 3,
      },
      py: { xs: 1.5, md: 0 },
    },

    [`&.${bannerClasses.fullWidthHeading}`]: {
      width: '100%',
    },

    [`.${bannerClasses.fullSize} &`]: {
      transform: {
        xs: `translateY(calc(-${WHITE_BAR_HEIGHT.MOBILE / 2}px - 65px))`,
        lg: `translateY(calc(-${WHITE_BAR_HEIGHT.DESKTOP / 2}px - 25px))`,
      },
      gap: {
        xs: 0,
        md: 1.5,
      },
    },
  },
  contentWrapperFanatics: {
    [`.${bannerClasses.fullSize} &`]: {
      gap: { xs: 1, md: 2.5 },
    },
  },
  subContentWrapper: {
    width: CONTENT_WRAPPER_WIDTH,
    maxWidth: 'calc(100vw - 40px)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: {
      xs: 2,
      md: 3,
    },
    [`.${bannerClasses.fullSize} &`]: {
      gap: {
        xs: 3,
        md: 5,
      },
    },
  },

  subHeading: {
    typography: {
      xs: 'textMd',
      md: 'textLg',
      lg: 'displayXs',
    },
    fontWeight: FONT_WEIGHTS.LIGHT,
    lineHeight: '120%',
    color: 'text.placeHolderSubtle',

    [`.${bannerClasses.compact} &`]: {
      typography: {
        xs: 'textMd',
        md: 'textXl',
      },
    },
    [`.${bannerClasses.fullSize} &`]: {
      typography: {
        xs: 'textLg',
        md: 'textXl',
        lg: 'display2Xs',
      },
      color: 'text.primaryWarmIvory',
    },
  },
  subHeadingFanatics: {
    [`.${bannerClasses.fullSize} &`]: {
      typography: {
        xs: 'textSm',
        md: 'textLg',
        lg: 'textXl',
      },
    },
  },
  animateHeading: {
    opacity: 0,
    animation: `${slideUpFadeIn} 2s cubic-bezier(0.22,1,0.36,1) 0s forwards`,
  },
  animateSubHeading: {
    opacity: 0,
    animation: `${slideUpFadeIn} 2s cubic-bezier(0.22,1,0.36,1) 0.18s forwards`,
  },
  animateSearch: {
    opacity: 0,
    width: '100%',
    animation: `${slideUpFadeIn} 2s cubic-bezier(0.22,1,0.36,1) 0.36s forwards`,
  },
  animateChips: {
    opacity: 0,
    animation: `${slideUpFadeIn} 2s cubic-bezier(0.22,1,0.36,1) 0.54s forwards`,
  },
  fanaticsIcon: {
    color: 'text.primaryWarmIvory',
    display: { xs: 'none', lg: 'block' },
    mb: 1,
  },
}
